import { useEffect, useMemo, useRef, useState } from 'react';
import FormatConverterProject from './apps/format-converter/FormatConverterProject';
import StorageProject from './apps/storage/StorageProject';
import VideoDownloaderProject from './apps/video-downloader/VideoDownloaderProject';
import { NAV_ITEMS, PROJECTS } from './config/workspace';
import AuthScreen from './components/workspace/AuthScreen';
import LoadingScreen from './components/workspace/LoadingScreen';
import ProjectsDashboard from './components/workspace/ProjectsDashboard';
import Sidebar from './components/workspace/Sidebar';
import useWorkspaceAuth from './hooks/useWorkspaceAuth';

const DEFAULT_VIEW = 'dashboard';
const VALID_VIEWS = new Set(NAV_ITEMS.map((item) => item.key));

function getViewFromPath(pathname) {
  if (!pathname || pathname === '/' || pathname === '/dashboard') return DEFAULT_VIEW;

  const [segment] = pathname.replace(/^\/+/, '').split('/');
  return VALID_VIEWS.has(segment) ? segment : DEFAULT_VIEW;
}

function getPathFromView(view) {
  return view === DEFAULT_VIEW ? '/' : `/${view}`;
}

function renderActiveProject(activeView, user) {
  if (activeView === 'format-converter') return <FormatConverterProject user={user} />;
  if (activeView === 'video-downloader') return <VideoDownloaderProject user={user} />;
  if (activeView === 'storage') return <StorageProject user={user} />;
  return <FormatConverterProject user={user} />;
}

function App() {
  const { user, isAuthLoading, username, password, authError, setUsername, setPassword, handleAuth, handleSignOut: signOutUser } =
    useWorkspaceAuth();
  const [activeView, setActiveView] = useState(() =>
    typeof window !== 'undefined' ? getViewFromPath(window.location.pathname) : DEFAULT_VIEW
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const profileMenuRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePopState = () => {
      setActiveView(getViewFromPath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const navigateToView = (nextView, options = {}) => {
    const resolvedView = VALID_VIEWS.has(nextView) ? nextView : DEFAULT_VIEW;
    const nextPath = getPathFromView(resolvedView);
    const { replace = false } = options;

    setActiveView(resolvedView);

    if (typeof window === 'undefined') return;

    if (window.location.pathname !== nextPath) {
      if (replace) {
        window.history.replaceState({}, '', nextPath);
      } else {
        window.history.pushState({}, '', nextPath);
      }
    }
  };

  useEffect(() => {
    if (!isProfileMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsProfileMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isProfileMenuOpen]);

  const handleSignOut = async () => {
    setIsProfileMenuOpen(false);
    setIsSidebarOpen(false);
    await signOutUser();
  };

  const profileName = useMemo(() => (user?.email || '').split('@')[0] || 'user', [user]);
  const profileInitial = profileName.charAt(0).toUpperCase();

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return (
      <AuthScreen
        username={username}
        password={password}
        authError={authError}
        onUsernameChange={setUsername}
        onPasswordChange={setPassword}
        onSubmit={handleAuth}
      />
    );
  }

  return (
    <main className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen">
        <Sidebar
          navItems={NAV_ITEMS}
          activeView={activeView}
          setActiveView={navigateToView}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          profileMenuRef={profileMenuRef}
          isProfileMenuOpen={isProfileMenuOpen}
          setIsProfileMenuOpen={setIsProfileMenuOpen}
          profileInitial={profileInitial}
          profileName={profileName}
          onSignOut={handleSignOut}
        />

        <section className="min-w-0 flex-1 p-4 pt-16 lg:p-6">
          {activeView === 'dashboard' ? (
            <ProjectsDashboard projects={PROJECTS} onOpenProject={navigateToView} />
          ) : (
            renderActiveProject(activeView, user)
          )}
        </section>
      </div>
    </main>
  );
}

export default App;
